import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Package, PackageCheck, Truck, MapPin, CheckCircle2, AlertTriangle,
  Undo2, HelpCircle, Loader2, ExternalLink, Sparkles, Clock, RefreshCw
} from 'lucide-react';
import { Button } from './ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Mapping stato normalizzato → icona + colore + label italiana
const STATUS_META = {
  CREATED:            { Icon: Package,        color: 'text-slate-500',   bg: 'bg-slate-100',   label: 'Spedizione registrata' },
  LABEL_CREATED:      { Icon: Package,        color: 'text-slate-500',   bg: 'bg-slate-100',   label: 'Etichetta creata' },
  PICKED_UP:          { Icon: PackageCheck,   color: 'text-teal-600',    bg: 'bg-teal-100',    label: 'Preso in carico dal corriere' },
  IN_TRANSIT:         { Icon: Truck,          color: 'text-blue-600',    bg: 'bg-blue-100',    label: 'In transito' },
  OUT_FOR_DELIVERY:   { Icon: MapPin,         color: 'text-amber-600',   bg: 'bg-amber-100',   label: 'In consegna' },
  DELIVERY_ATTEMPTED: { Icon: Clock,          color: 'text-orange-600',  bg: 'bg-orange-100',  label: 'Consegna tentata' },
  DELIVERED:          { Icon: CheckCircle2,   color: 'text-emerald-600', bg: 'bg-emerald-100', label: 'Consegnato' },
  EXCEPTION:          { Icon: AlertTriangle,  color: 'text-red-600',     bg: 'bg-red-100',     label: 'Anomalia' },
  LOST:               { Icon: AlertTriangle,  color: 'text-red-700',     bg: 'bg-red-100',     label: 'Smarrito' },
  DAMAGED:            { Icon: AlertTriangle,  color: 'text-red-600',     bg: 'bg-red-100',     label: 'Danneggiato' },
  RETURN_TO_SENDER:   { Icon: Undo2,          color: 'text-purple-600',  bg: 'bg-purple-100',  label: 'In restituzione' },
  UNKNOWN:            { Icon: HelpCircle,     color: 'text-slate-500',   bg: 'bg-slate-100',   label: 'Stato non disponibile' },
};

const formatItalianDate = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString('it-IT', { day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
};

/**
 * ShipmentTimeline — mostra al cliente lo stato della spedizione con timeline verticale animata.
 * Se non ci sono ancora eventi (o corriere EXTERNAL), mostra il minimo utile: tracking + link.
 */
export const ShipmentTimeline = ({ orderId, compact = false }) => {
  const [shipments, setShipments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    if (!orderId) return;
    try {
      const r = await fetch(`${API_URL}/api/shipments/order/${orderId}`);
      if (!r.ok) throw new Error('load failed');
      const data = await r.json();
      setShipments(Array.isArray(data) ? data : []);
    } catch (e) {
      // Silent fail: se non c'è shipment non mostriamo nulla
      setShipments([]);
    }
  };

  useEffect(() => {
    setLoading(true);
    load().finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await load();
    setTimeout(() => setRefreshing(false), 400);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8 text-slate-400">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Caricamento tracking...
      </div>
    );
  }

  if (shipments.length === 0) {
    return null;
  }

  return (
    <div className="space-y-6">
      {shipments.map((s) => (
        <ShipmentCard key={s.id} shipment={s} compact={compact} onRefresh={handleRefresh} refreshing={refreshing} />
      ))}
    </div>
  );
};

const ShipmentCard = ({ shipment, compact, onRefresh, refreshing }) => {
  const statusMeta = STATUS_META[shipment.status] || STATUS_META.UNKNOWN;
  const { Icon } = statusMeta;
  const isExternal = shipment.tracking_provider_type === 'EXTERNAL';
  const events = shipment.events || [];
  const hasRealEvents = events.some((e) => e.event_type !== 'CREATED');

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm"
      data-testid={`shipment-card-${shipment.id}`}
    >
      {/* Header con stato principale */}
      <div className="relative p-5 bg-gradient-to-br from-slate-50 to-white border-b border-slate-100">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <motion.div
              initial={{ scale: 0.8, rotate: -8 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ type: 'spring', damping: 12 }}
              className={`w-12 h-12 rounded-2xl ${statusMeta.bg} flex items-center justify-center shadow-sm`}
            >
              <Icon className={`w-6 h-6 ${statusMeta.color}`} />
            </motion.div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h4 className="font-bold text-slate-900">{statusMeta.label}</h4>
                {shipment.tracking_provider_type === 'INTEGRATED' && (
                  <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-emerald-500 text-white text-[10px] font-bold rounded-full">
                    <Sparkles className="w-2.5 h-2.5" /> LIVE
                  </span>
                )}
              </div>
              <p className="text-xs text-slate-500 mt-0.5">
                {shipment.carrier_name}
                {shipment.tracking_number && (
                  <> · <span className="font-mono text-slate-700">{shipment.tracking_number}</span></>
                )}
              </p>
            </div>
          </div>
          {shipment.tracking_provider_type === 'INTEGRATED' && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onRefresh}
              className="text-slate-400 hover:text-teal-600"
              disabled={refreshing}
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            </Button>
          )}
        </div>
      </div>

      <div className="p-5">
        {/* External (Altro corriere) → link diretto */}
        {isExternal && shipment.tracking_url && (
          <a
            href={shipment.tracking_url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-between gap-2 px-4 py-3 rounded-xl bg-teal-50 border border-teal-200 hover:bg-teal-100 transition-colors group"
            data-testid={`track-external-${shipment.id}`}
          >
            <div className="flex items-center gap-2 min-w-0">
              <Truck className="w-4 h-4 text-teal-700 flex-shrink-0" />
              <span className="text-sm font-medium text-teal-900 truncate">
                Traccia sul sito di {shipment.carrier_name}
              </span>
            </div>
            <ExternalLink className="w-4 h-4 text-teal-700 group-hover:translate-x-0.5 transition-transform" />
          </a>
        )}

        {/* Timeline verticale eventi */}
        {hasRealEvents && (
          <div className="mt-4 relative">
            <div className="absolute left-[15px] top-2 bottom-2 w-px bg-slate-200" />
            <ul className="space-y-4">
              {[...events].reverse().map((event, i) => {
                const eventMeta = STATUS_META[event.event_type] || STATUS_META.UNKNOWN;
                const EventIcon = eventMeta.Icon;
                const isLast = i === 0;
                return (
                  <motion.li
                    key={event.id}
                    initial={{ opacity: 0, x: -8 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.04 }}
                    className="relative pl-11"
                  >
                    <div className={`absolute left-0 top-0 w-8 h-8 rounded-full ${isLast ? eventMeta.bg + ' shadow-md ring-2 ring-white' : 'bg-white border-2 border-slate-200'} flex items-center justify-center`}>
                      <EventIcon className={`w-4 h-4 ${isLast ? eventMeta.color : 'text-slate-400'}`} />
                    </div>
                    <div className={`text-sm font-medium ${isLast ? 'text-slate-900' : 'text-slate-600'}`}>
                      {eventMeta.label}
                      {event.description && event.description !== eventMeta.label && (
                        <span className="text-slate-500 font-normal"> — {event.description}</span>
                      )}
                    </div>
                    <div className="text-xs text-slate-400 mt-0.5 flex items-center gap-2">
                      {event.event_timestamp && <span>{formatItalianDate(event.event_timestamp)}</span>}
                      {event.location && <><span>·</span><span>{event.location}</span></>}
                    </div>
                  </motion.li>
                );
              })}
            </ul>
          </div>
        )}

        {/* Empty state per shipment appena creato senza eventi reali */}
        {!hasRealEvents && !isExternal && (
          <div className="mt-4 flex items-start gap-3 p-3 rounded-lg bg-slate-50 border border-slate-100">
            <Clock className="w-4 h-4 text-slate-400 mt-0.5" />
            <div className="text-xs text-slate-600">
              Il pacco è stato registrato. Gli aggiornamenti del corriere appariranno qui non appena disponibili.
            </div>
          </div>
        )}

        {/* CTA finale per external senza timeline */}
        {isExternal && !hasRealEvents && !shipment.tracking_url && (
          <div className="mt-4 text-xs text-slate-500">
            Tracking non disponibile online per questo corriere.
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default ShipmentTimeline;
